import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { QuizSession } from '@/components/QuizSession';
import { CircularLoader } from '@/components/CircularLoader';
import styles from './index.module.scss';

export const Route = createFileRoute('/review-center/$quizId/practice')({
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: unknown; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/login' });
    }
  },
  component: PracticeQuizPage,
});

function PracticeQuizPage() {
  const { quizId } = Route.useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['pendingQuiz', quizId],
    queryFn: async () => {
      const response = await api.listPendingQuizzes();
      const quiz = response.pendingQuizzes.find(q => q.id === quizId);
      if (!quiz) {
        throw new Error('Quiz not found');
      }
      return quiz;
    },
  });

  if (isLoading) {
    return (
      <div className={styles.reviewCenterPage__loading}>
        <CircularLoader />
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={styles.reviewCenterPage__error}>
        <p>Quiz not found or failed to load</p>
        <button onClick={() => navigate({ to: '/review-center' })}>
          Back to Review Center
        </button>
      </div>
    );
  }

  if (data.status !== 'ready' || !data.quiz_data?.questions) {
    return (
      <div className={styles.reviewCenterPage__error}>
        <p>This quiz is not ready for practice</p>
        <button onClick={() => navigate({ to: '/review-center' })}>
          Back to Review Center
        </button>
      </div>
    );
  }

  return (
    <QuizSession
      questions={data.quiz_data.questions}
      title={data.quiz_name || 'Practice Quiz'}
      skipAnalytics={true}
      onExit={() => navigate({ to: '/review-center' })}
    />
  );
}
